import type {
  ILocationBreakdown,
  ILocationCount,
} from '~/types/msService/models'
import type { IEntitySelectItem, TEntityId } from '~/types/models'

/**
 * Count the locations of `msServices`. The CALLER picks the set: a kit passes
 * its live items (getLocationBreakdownFromKitItemServices).
 *
 * A tie for most common goes to `preferredLocationId` -- the kit row's own
 * location -- when it is one of the tied, so a tie never moves the kit row;
 * otherwise to the tied location whose msService comes FIRST in `msServices`.
 *
 * Modelled on getTermBreakdown (TermBreakdown.ts), with one difference: a kit
 * row stores no term, but it does store a location, and that stored location
 * is meant to be this `majorityLocation`.
 */
export function getLocationBreakdown(
  msServices: { location?: IEntitySelectItem | null }[],
  { preferredLocationId }: { preferredLocationId?: TEntityId | null } = {}
): ILocationBreakdown {
  // A Map keeps first-seen order, and the sort below is stable, so a tie that
  // the preferred location does not settle stays in that order.
  const countById = new Map<TEntityId, ILocationCount>()
  for (const msService of msServices) {
    const location = msService.location
    if (!location?.id) {
      continue
    }
    const locationCount = countById.get(location.id)
    if (locationCount) {
      locationCount.count++
    } else {
      countById.set(location.id, { location, count: 1 })
    }
  }
  const counts = Array.from(countById.values())
  counts.sort(
    (a, b) =>
      b.count - a.count ||
      Number(b.location.id === preferredLocationId) -
        Number(a.location.id === preferredLocationId)
  )
  return {
    majorityLocation: counts[0]?.location,
    counts,
    doesVary: counts.length > 1,
  }
}

/**
 * A varied kit row's Location cell: the majority location marked "+"
 * ("BOSTON HQ+"), so it is not read as every item's location.
 */
export function getDisplayLocationLabel(item: {
  location?: IEntitySelectItem | null
  locationBreakdown?: ILocationBreakdown
}) {
  const label = item.location?.label
  return item.locationBreakdown?.doesVary ? `${label}+` : label
}

/** The Location cell menu's rows: "3 services" / "Boston HQ", most common first. */
export function getLocationBreakdownRows(
  locationBreakdown?: ILocationBreakdown
) {
  return (locationBreakdown?.counts ?? []).map(({ location, count }) => ({
    count: `${count} service${count === 1 ? '' : 's'}`,
    label: location.label,
  }))
}
